

import React, { useEffect, useState, useRef } from "react";
import { useVideoQualityOptions, useMediaRemote } from "@vidstack/react";
import {
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ClickAwayListener,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded"; // quality icon
import CheckIcon from "@mui/icons-material/Check";

const QualitySelector: React.FC = () => {
  const qualities = useVideoQualityOptions();
  const remote = useMediaRemote();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number>(-1); // -1 for Auto
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem("selectedQuality");
    const storedIndex = stored !== null ? parseInt(stored, 10) : -1;

    if (!isNaN(storedIndex) && (storedIndex === -1 || qualities[storedIndex])) {
      setSelected(storedIndex);
      if (storedIndex !== -1) remote.changeQuality(storedIndex);
    }
  }, [qualities, remote]);

  const handleSelect = (index: number) => {
    setSelected(index);
    localStorage.setItem("selectedQuality", index.toString());
    index = index - 1;
    remote.changeQuality(index);
    setOpen(false);
  };

  const togglePopup = () => setOpen((prev) => !prev);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative", // Needed to contain the popup
        zIndex: 10,
      }}
    >
      <IconButton
        onClick={togglePopup}
        sx={{
          color: "#fff",
          "&:hover": {
            cursor: "pointer",
            backgroundColor: "#ffffff57",
            borderRadius: "10px",
          },
        }}
      >
        <TuneRoundedIcon sx={{ color: "#fff" }} />
      </IconButton>
      {open && (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              bottom: "40px", // height above control bar
              right: 0,
              backgroundColor: "#1e1e1e",
              color: "#fff",
              borderRadius: 2,
              width: 180,
              boxShadow: 3,
              p: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              px={1}
              py={0.5}
              sx={{ opacity: 0.7, fontWeight: "600", letterSpacing: "0.5px" }}
            >
              Quality
            </Typography>

            <List
              dense
              disablePadding
              sx={{ borderTop: "1px solid #fefefe29" }}
            >
              {[...qualities]
                .filter((q) => q.label.toLowerCase() !== "auto")
                .map((q, index) => {
                  const actualIndex = qualities.length - 1 - index;
                  return (
                    <ListItem key={actualIndex} disablePadding>
                      <ListItemButton
                        selected={selected === actualIndex}
                        onClick={() => handleSelect(actualIndex)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          pl: 2,
                          pr: 1,
                        }}
                      >
                        <ListItemText
                          primary={q.label}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight:
                              selected === actualIndex ? "bold" : "normal",
                          }}
                        />
                        {selected === actualIndex && (
                          <CheckIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              <ListItem disablePadding>
                <ListItemButton
                  selected={selected === -1}
                  onClick={() => handleSelect(-1)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pl: 2,
                    pr: 1,
                  }}
                >
                  <ListItemText
                    primary="Auto"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: selected === -1 ? "bold" : "normal",
                    }}
                  />
                  {selected === -1 && (
                    <CheckIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                  )}
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default QualitySelector;
